import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useScroll, useTransform, useInView, useMotionValue, animate } from 'framer-motion';
import Hero from '../assets/Hero1.mp4';

/* ============================================================
   YogaRefer — Home Page
   Elegant • Spiritual • Modern • Premium • Nature-inspired
   Palette:
     --warm-white   #FBF8F3
     --sage         #A9BFA3
     --sage-deep    #6F8A6A
     --sand         #E7D9C4
     --lavender     #C9C0DC
     --gold         #C9A24B
     --ink          #2E3530
   ============================================================ */

const TOKENS = {
  warmWhite: '#FBF8F3',
  cream: '#F3ECE0',
  sand: '#E7D9C4',
  sage: '#A9BFA3',
  sageDeep: '#6F8A6A',
  lavender: '#C9C0DC',
  lavenderMist: '#EDE8F4',
  gold: '#C9A24B',
  ink: '#2E3530',
  inkSoft: '#5A6258',
  serif: "'Cormorant Garamond', 'Playfair Display', Georgia, serif",
  sans: "'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

/* ---------- FAQ content ---------- */
const FAQ_GROUPS = [
  {
    title: 'General Questions',
    items: [
      { q: 'How many images can I upload?', a: '10 per day, 50 per week, total 200.' },
      { q: 'What camera do I need?', a: 'Your mobile phone is sufficient! HD clarity is good.' },
      { q: 'Why are AI images not allowed?', a: 'We want to show only real human yoga practice.' },
    ],
  },
  {
    title: 'Reward Questions',
    items: [
      { q: 'How will my name be displayed?', a: '"Rajesh - @rajeshyoga" (your Insta handle if you wish).' },
      { q: 'What happens after 6 months?', a: 'The image and your name are permanent! Only your link will expire.' },
      { q: 'Can I delete my image?', a: 'Yes! Click "Delete" in your Dashboard. It will be removed in 15 days.' },
    ],
  },
  {
    title: 'Extension Questions',
    items: [
      { q: 'If I reach the 24-month limit?', a: 'The image is permanent! Only the link expires. You cannot upload the same image again.' },
    ],
  },
];

/* ---------- Reusable: FAQ accordion item ---------- */
const FaqItem = ({ q, a, open, onToggle }) => (
  <div style={{
    borderRadius: 18,
    background: open ? '#fff' : 'rgba(255,255,255,0.6)',
    border: `1px solid ${open ? TOKENS.sage : TOKENS.sand}`,
    boxShadow: open ? '0 14px 34px rgba(46,53,48,0.08)' : 'none',
    transition: 'all 0.3s ease',
    overflow: 'hidden',
  }}>
    <button
      onClick={onToggle}
      aria-expanded={open}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
        padding: '20px 24px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
        fontFamily: TOKENS.sans, fontSize: '1.02rem', fontWeight: 600, color: TOKENS.ink,
      }}
    >
      <span>{q}</span>
      <motion.span
        animate={{ rotate: open ? 45 : 0 }}
        transition={{ duration: 0.25 }}
        style={{
          flexShrink: 0, width: 30, height: 30, borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: open ? TOKENS.sageDeep : TOKENS.cream,
          color: open ? '#fff' : TOKENS.sageDeep,
          fontSize: '1.2rem', fontWeight: 400, lineHeight: 1,
        }}
      >
        +
      </motion.span>
    </button>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
        >
          <p style={{ margin: 0, padding: '0 24px 22px', color: TOKENS.inkSoft, fontSize: '0.97rem', lineHeight: 1.7 }}>
            {a}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

/* ---------- Reusable: animated counter ---------- */
const Counter = ({ to, suffix = '', duration = 2.2 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const mv = useMotionValue(0);
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(mv, to, {
      duration,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setVal(Math.floor(v)),
    });
    return controls.stop;
  }, [inView, to, duration, mv]);

  return (
    <span ref={ref}>
      {val.toLocaleString()}
      {suffix}
    </span>
  );
};

/* ---------- Floating organic blobs ---------- */
const Blob = ({ style, delay = 0 }) => (
  <motion.div
    aria-hidden
    style={{
      position: 'absolute',
      borderRadius: '50%',
      filter: 'blur(60px)',
      pointerEvents: 'none',
      ...style,
    }}
    animate={{ y: [0, -30, 0], x: [0, 20, 0], scale: [1, 1.08, 1] }}
    transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut', delay }}
  />
);

/* ============================================================ */

const Home = () => {
  const [openFaq, setOpenFaq] = useState('0-0');
  const { scrollYProgress } = useScroll();
  const heroY = useTransform(scrollYProgress, [0, 0.4], ['0%', '20%']);
  const heroFade = useTransform(scrollYProgress, [0, 0.3], [1, 0.4]);

  /* ---------- Styles ---------- */
  const container = {
    width: '100vw',
    minHeight: '100vh',
    fontFamily: TOKENS.sans,
    color: TOKENS.ink,
    background: TOKENS.warmWhite,
    overflowX: 'hidden',
    position: 'relative',
    left: '50%',
    right: '50%',
    marginLeft: '-50vw',
    marginRight: '-50vw',
    marginTop: '-10vh',
  };

  const sectionTitle = {
    fontFamily: TOKENS.serif,
    fontWeight: 400,
    fontSize: 'clamp(2rem, 5vw, 4rem)',
    lineHeight: 1.05,
    letterSpacing: '-0.01em',
    color: TOKENS.ink,
    margin: 0,
  };

  const eyebrow = {
    fontSize: '0.78rem',
    letterSpacing: '0.32em',
    textTransform: 'uppercase',
    color: TOKENS.sageDeep,
    fontWeight: 500,
    marginBottom: 18,
  };

  const lead = {
    fontFamily: TOKENS.sans,
    fontSize: 'clamp(1rem, 1.4vw, 1.15rem)',
    lineHeight: 1.8,
    color: TOKENS.inkSoft,
    fontWeight: 300,
  };

  /* ---------- Data ---------- */
  const worldCards = [
    { title: 'Guided Practices', img: 'https://images.unsplash.com/photo-1545389336-cf090694435e?w=1200&q=80', desc: 'Flow with teachers who meet you where you are.' },
    { title: 'Meditation', img: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=1200&q=80', desc: 'Stillness practices for a quieter mind.' },
    { title: 'Wellness Resources', img: 'https://images.unsplash.com/photo-1540202404-1b927e27fa8b?w=1200&q=80', desc: 'Reading, rituals & breathwork to live well.' },
    { title: 'Teacher Connections', img: 'https://images.unsplash.com/photo-1599447421416-3414500d18a5?w=1200&q=80', desc: 'Find the guide whose voice feels like home.' },
    { title: 'Events & Workshops', img: 'https://images.unsplash.com/photo-1593810451137-5dc55105dace?w=1200&q=80', desc: 'Gather, learn, and breathe together.' },
    { title: 'Mindful Living', img: 'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?w=1200&q=80', desc: 'Small daily practices, lasting transformation.' },
  ];

  const journeys = [
    { tag: 'Calm', title: 'Stress Relief', desc: 'Soft, restorative sequences to release the week.', img: 'https://images.unsplash.com/photo-1474418397713-7ede21d49118?w=1200&q=80' },
    { tag: 'Open', title: 'Flexibility Journey', desc: '30 days of slow unfolding — hips, spine, heart.', img: 'https://images.unsplash.com/photo-1518611012118-696072aa579a?w=1200&q=80' },
    { tag: 'Present', title: 'Mindfulness Practice', desc: 'Daily moments of stillness and awareness.', img: 'https://images.unsplash.com/photo-1528319725582-ddc096101511?w=1200&q=80' },
    { tag: 'Ritual', title: 'Daily Yoga Routine', desc: 'A 20-minute flow to begin every morning.', img: 'https://images.unsplash.com/photo-1588286840104-8957b019727f?w=1200&q=80' },
    { tag: 'Strong', title: 'Inner Strength', desc: 'Build steady power, breath by breath.', img: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=1200&q=80' },
  ];

  const stats = [
    { n: 24500, suffix: '+', label: 'Active Practitioners' },
    { n: 180000, suffix: '+', label: 'Yoga Sessions Shared' },
    { n: 1200, suffix: '+', label: 'Wellness Resources' },
    { n: 96, suffix: '%', label: 'Feel More Connected' },
  ];

  const testimonials = [
    { name: 'Anaya R.', role: 'Practitioner • 2 years', img: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400&q=80', quote: 'I came for the postures and stayed for the people. This community held me through the hardest year of my life.' },
    { name: 'Marcus T.', role: 'New to yoga', img: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&q=80', quote: 'I never thought I’d be the kind of person who meditates. Now my mornings begin with breath, not noise.' },
    { name: 'Leila S.', role: 'Teacher', img: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&q=80', quote: 'YogaRefer feels like a sangha — warm, sincere, and quietly transformative.' },
  ];

  const events = [
    { date: 'Jun 14', title: 'Sunrise Vinyasa Retreat', place: 'Rishikesh, India', img: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200&q=80' },
    { date: 'Jun 22', title: 'Breathwork Online Circle', place: 'Live • Worldwide', img: 'https://images.unsplash.com/photo-1593811167562-9cef47bfc4d7?w=1200&q=80' },
    { date: 'Jul 05', title: 'Restore & Reset Workshop', place: 'Bali, Indonesia', img: 'https://images.unsplash.com/photo-1540206395-68808572332f?w=1200&q=80' },
    { date: 'Jul 19', title: 'Full Moon Community Flow', place: 'Local Chapters', img: 'https://images.unsplash.com/photo-1519834785169-98be25ec3f84?w=1200&q=80' },
  ];

  /* ============================================================ */

  return (
    <div style={container}>
      {/* ============ 1. CINEMATIC HERO ============ */}
      <section style={{ position: 'relative', width: '100%', height: '100vh', minHeight: 640, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', background: '#0b0f0c' }}>
        <motion.div style={{ position: 'absolute', inset: 0, y: heroY, opacity: heroFade }}>
          <motion.video
            autoPlay loop muted playsInline
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            initial={{ scale: 1.25, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }}
          >
            <source src={Hero} type="video/mp4" />
          </motion.video>
        </motion.div>

        {/* Warm gradient veil */}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(20,30,25,0.35) 0%, rgba(20,30,25,0.15) 40%, rgba(20,30,25,0.65) 100%)' }} />

        {/* Floating organic glows */}
        <Blob style={{ width: 320, height: 320, top: '12%', left: '8%', background: 'rgba(201,162,75,0.25)' }} delay={0} />
        <Blob style={{ width: 380, height: 380, bottom: '10%', right: '6%', background: 'rgba(201,192,220,0.22)' }} delay={3} />
        <Blob style={{ width: 240, height: 240, top: '40%', right: '20%', background: 'rgba(169,191,163,0.18)' }} delay={6} />

        <motion.div
          style={{ position: 'relative', zIndex: 3, textAlign: 'center', padding: '0 24px', maxWidth: 980 }}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
        >
          <motion.div
            initial={{ opacity: 0, letterSpacing: '0.1em' }}
            animate={{ opacity: 1, letterSpacing: '0.42em' }}
            transition={{ delay: 0.3, duration: 1.2 }}
            style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase', marginBottom: 28, fontWeight: 400 }}
          >
            yogarefer · a living community
          </motion.div>

          <h1 style={{
            fontFamily: TOKENS.serif,
            fontWeight: 300,
            fontSize: 'clamp(2.2rem, 6.2vw, 5.5rem)',
            color: '#fff',
            lineHeight: 1.05,
            letterSpacing: '-0.015em',
            margin: 0,
          }}>
            Find Balance.<br />
            <span style={{ fontStyle: 'italic', color: TOKENS.sand }}>Connect Deeply.</span><br />
            Live Mindfully.
          </h1>

          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: 1 }}
            style={{ marginTop: 32, fontSize: 'clamp(1rem, 1.6vw, 1.25rem)', lineHeight: 1.7, color: 'rgba(255,255,255,0.88)', fontWeight: 300, maxWidth: 640, margin: '32px auto 0' }}
          >
            Join a growing community of yoga practitioners, teachers, and wellness seekers on a journey toward inner peace.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.9 }}
            style={{ marginTop: 44, display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}
          >
            <a href="https://yogarefer.com/" target="_blank" rel="noopener noreferrer">
  <motion.button
    whileHover={{ y: -2, boxShadow: '0 18px 40px rgba(201,162,75,0.45)' }}
    whileTap={{ scale: 0.98 }}
    style={{
      padding: '16px 36px', borderRadius: 999, border: 'none', cursor: 'pointer',
      background: `linear-gradient(135deg, ${TOKENS.gold} 0%, #d9b66a 100%)`,
      color: '#1c1f1b', fontWeight: 600, fontSize: '0.98rem', letterSpacing: '0.04em',
      boxShadow: '0 10px 30px rgba(201,162,75,0.35)',
    }}
  >
    Explore Community
  </motion.button>
</a>
           <motion.button
  onClick={() => window.location.href = '/login'}
  whileHover={{ y: -2, background: 'rgba(255,255,255,0.12)' }}
  whileTap={{ scale: 0.98 }}
  style={{
    padding: '16px 36px', borderRadius: 999, cursor: 'pointer',
    background: 'transparent', color: '#fff',
    border: '1px solid rgba(255,255,255,0.55)',
    fontWeight: 500, fontSize: '0.98rem', letterSpacing: '0.04em',
    backdropFilter: 'blur(6px)',
  }}
>
  Begin Your Journey
</motion.button>
          </motion.div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2, duration: 1 }}
          style={{ position: 'absolute', bottom: 36, left: '50%', transform: 'translateX(-50%)', zIndex: 3, color: 'rgba(255,255,255,0.75)', fontSize: '0.7rem', letterSpacing: '0.3em', textTransform: 'uppercase', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}
        >
          <span>scroll</span>
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            style={{ width: 1, height: 40, background: 'rgba(255,255,255,0.6)' }}
          />
        </motion.div>
      </section>

      {/* ============ 2. COMMUNITY STORY ============ */}
      <section style={{ position: 'relative', padding: 'clamp(80px, 12vw, 160px) clamp(24px, 6vw, 80px)', background: TOKENS.warmWhite, overflow: 'hidden' }}>
        <Blob style={{ width: 480, height: 480, top: '-10%', right: '-10%', background: 'rgba(169,191,163,0.18)' }} />
        <div style={{ maxWidth: 1280, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 380px), 1fr))', gap: 'clamp(40px, 6vw, 90px)', alignItems: 'center', position: 'relative' }}>
          <motion.div
            initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          >
            <div style={eyebrow}>· Our Story</div>
            <h2 style={sectionTitle}>
              Yoga Is <span style={{ fontStyle: 'italic', color: TOKENS.sageDeep }}>Better Together</span>.
            </h2>
            <p style={{ ...lead, marginTop: 28 }}>
              We began with a simple belief: that the practice deepens when it is shared. YogaRefer is a
              gathering place — for the curious, the devoted, the just-beginning — to learn from each other,
              breathe in rhythm, and slowly build a more mindful life.
            </p>
            <p style={{ ...lead, marginTop: 18 }}>
              No leaderboards. No metrics of worth. Only teachers who listen, practitioners who care, and a
              quiet conviction that growth happens in community.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.94 }} whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: '-80px' }} transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            style={{ position: 'relative', aspectRatio: '4/5', borderRadius: '50% 50% 48% 52% / 56% 44% 56% 44%', overflow: 'hidden', boxShadow: '0 40px 80px -20px rgba(46,53,48,0.25)' }}
          >
            <img
              src="https://images.unsplash.com/photo-1599901860904-17e6ed7083a0?w=1400&q=80"
              alt="Community of practitioners in soft morning light"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </motion.div>
        </div>
      </section>

      {/* ============ 3. EXPLORE THE WORLD OF YOGA ============ */}
      <section style={{ padding: 'clamp(80px, 12vw, 160px) clamp(24px, 6vw, 80px)', background: `linear-gradient(180deg, ${TOKENS.cream} 0%, ${TOKENS.warmWhite} 100%)` }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.9 }}
            style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 70px' }}
          >
            <div style={eyebrow}>· Explore</div>
            <h2 style={sectionTitle}>The world of <span style={{ fontStyle: 'italic', color: TOKENS.sageDeep }}>yoga</span></h2>
            <p style={{ ...lead, marginTop: 22 }}>
              Six doorways into a deeper, calmer way of being.
            </p>
          </motion.div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', gap: 28 }}>
            {worldCards.map((c, i) => (
              <motion.article
                key={c.title}
                initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.7, delay: (i % 3) * 0.12, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -8 }}
                style={{ position: 'relative', borderRadius: 24, overflow: 'hidden', aspectRatio: '4/5', cursor: 'pointer', boxShadow: '0 20px 50px -25px rgba(46,53,48,0.4)', background: '#000' }}
              >
                <motion.img
                  src={c.img} alt={c.title}
                  whileHover={{ scale: 1.08 }}
                  transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.92 }}
                />
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.7) 100%)' }} />
                <div style={{ position: 'absolute', left: 28, right: 28, bottom: 28, color: '#fff' }}>
                  <h3 style={{ fontFamily: TOKENS.serif, fontSize: '1.7rem', fontWeight: 400, margin: 0, letterSpacing: '-0.01em' }}>{c.title}</h3>
                  <p style={{ marginTop: 8, fontSize: '0.95rem', fontWeight: 300, color: 'rgba(255,255,255,0.85)', lineHeight: 1.5 }}>{c.desc}</p>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

       {/* ============ 7. DAILY INSPIRATION ============ */}
      <section style={{ position: 'relative', padding: 'clamp(100px, 14vw, 180px) clamp(24px, 6vw, 80px)', background: `radial-gradient(ellipse at top, ${TOKENS.sand} 0%, ${TOKENS.warmWhite} 70%)`, overflow: 'hidden', textAlign: 'center' }}>
        <Blob style={{ width: 500, height: 500, top: '20%', left: '50%', transform: 'translateX(-50%)', background: 'rgba(201,162,75,0.18)' }} />
        <Blob style={{ width: 280, height: 280, bottom: '5%', right: '8%', background: 'rgba(169,191,163,0.22)' }} delay={4} />

        <motion.div
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 1.2 }}
          style={{ position: 'relative', maxWidth: 880, margin: '0 auto' }}
        >
          <div style={{ ...eyebrow, color: TOKENS.gold, textAlign: 'center' }}>· Daily Inspiration</div>
          <p style={{
            fontFamily: TOKENS.serif, fontStyle: 'italic', fontWeight: 400,
            fontSize: 'clamp(1.6rem, 3.4vw, 2.8rem)', lineHeight: 1.35,
            color: TOKENS.ink, letterSpacing: '-0.01em', margin: 0,
          }}>
            “Yoga is the journey of the self, through the self, to the self.”
          </p>
          <div style={{ marginTop: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
            <span style={{ width: 40, height: 1, background: TOKENS.gold }} />
            <span style={{ fontSize: '0.85rem', letterSpacing: '0.25em', textTransform: 'uppercase', color: TOKENS.sageDeep }}>The Bhagavad Gita</span>
            <span style={{ width: 40, height: 1, background: TOKENS.gold }} />
          </div>
        </motion.div>
      </section>

      {/* ============ 4. FEATURED JOURNEYS ============ */}
      <section style={{ padding: 'clamp(80px, 12vw, 160px) 0', background: TOKENS.warmWhite }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 clamp(24px, 6vw, 80px)' }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.9 }}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 24, marginBottom: 60 }}
          >
            <div style={{ maxWidth: 560 }}>
              <div style={eyebrow}>· Journeys</div>
              <h2 style={sectionTitle}>Begin a <span style={{ fontStyle: 'italic', color: TOKENS.sageDeep }}>practice</span> that fits your life</h2>
            </div>
            <p style={{ ...lead, maxWidth: 360 }}>Curated paths, each a gentle invitation. Move at your own pace.</p>
          </motion.div>
        </div>

        <div style={{ overflowX: 'auto', scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch', paddingBottom: 20 }}>
          <div style={{ display: 'flex', gap: 24, padding: '0 clamp(24px, 6vw, 80px)', width: 'max-content' }}>
            {journeys.map((j, i) => (
              <motion.div
                key={j.title}
                initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.7, delay: i * 0.08 }}
                whileHover={{ y: -6 }}
                style={{ scrollSnapAlign: 'start', width: 'clamp(280px, 32vw, 380px)', borderRadius: 28, overflow: 'hidden', background: '#fff', boxShadow: '0 20px 50px -25px rgba(46,53,48,0.3)', cursor: 'pointer' }}
              >
                <div style={{ aspectRatio: '4/3', overflow: 'hidden' }}>
                  <motion.img
                    src={j.img} alt={j.title}
                    whileHover={{ scale: 1.07 }}
                    transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
                <div style={{ padding: '28px 28px 32px' }}>
                  <span style={{ display: 'inline-block', fontSize: '0.7rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: TOKENS.gold, fontWeight: 600, marginBottom: 12 }}>{j.tag}</span>
                  <h3 style={{ fontFamily: TOKENS.serif, fontSize: '1.7rem', fontWeight: 400, margin: 0, color: TOKENS.ink }}>{j.title}</h3>
                  <p style={{ marginTop: 10, color: TOKENS.inkSoft, fontSize: '0.98rem', lineHeight: 1.6, fontWeight: 300 }}>{j.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>



      {/* ============ 6. TESTIMONIALS ============
      <section style={{ padding: 'clamp(80px, 12vw, 160px) clamp(24px, 6vw, 80px)', background: TOKENS.warmWhite }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.9 }}
            style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 70px' }}
          >
            <div style={eyebrow}>· Voices</div>
            <h2 style={sectionTitle}>Stories of <span style={{ fontStyle: 'italic', color: TOKENS.sageDeep }}>quiet transformation</span></h2>
          </motion.div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', gap: 28 }}>
            {testimonials.map((t, i) => (
              <motion.figure
                key={t.name}
                initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.8, delay: i * 0.15 }}
                style={{ margin: 0, padding: '40px 36px', borderRadius: 24, background: TOKENS.cream, border: `1px solid ${TOKENS.sand}`, position: 'relative' }}
              >
                <div style={{ position: 'absolute', top: 24, right: 32, fontFamily: TOKENS.serif, fontSize: '4rem', color: TOKENS.gold, opacity: 0.35, lineHeight: 1 }}>“</div>
                <blockquote style={{ margin: 0, fontFamily: TOKENS.serif, fontSize: '1.25rem', lineHeight: 1.55, color: TOKENS.ink, fontWeight: 400, fontStyle: 'italic' }}>
                  {t.quote}
                </blockquote>
                <figcaption style={{ marginTop: 28, display: 'flex', alignItems: 'center', gap: 14 }}>
                  <img src={t.img} alt={t.name} style={{ width: 52, height: 52, borderRadius: '50%', objectFit: 'cover' }} />
                  <div>
                    <div style={{ fontWeight: 600, color: TOKENS.ink, fontSize: '0.98rem' }}>{t.name}</div>
                    <div style={{ fontSize: '0.82rem', color: TOKENS.inkSoft, marginTop: 2 }}>{t.role}</div>
                  </div>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section> */}

     

     
     

      {/* ============ 9. FINAL CTA ============ */}
      <section style={{ position: 'relative', padding: 'clamp(100px, 14vw, 180px) clamp(24px, 6vw, 80px)', overflow: 'hidden', background: `linear-gradient(135deg, ${TOKENS.sage} 0%, ${TOKENS.lavender} 60%, ${TOKENS.sand} 100%)` }}>
        <motion.div
          aria-hidden
          animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
          transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            position: 'absolute', inset: 0,
            background: `linear-gradient(120deg, rgba(255,255,255,0.0) 0%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0) 100%)`,
            backgroundSize: '200% 200%',
          }}
        />
        <Blob style={{ width: 380, height: 380, top: '-10%', left: '5%', background: 'rgba(255,255,255,0.25)' }} />
        <Blob style={{ width: 320, height: 320, bottom: '-8%', right: '8%', background: 'rgba(201,162,75,0.28)' }} delay={3} />

        <motion.div
          initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 1.1 }}
          style={{ position: 'relative', textAlign: 'center', maxWidth: 820, margin: '0 auto' }}
        >
          <div style={{ ...eyebrow, color: '#fff', opacity: 0.85 }}>· Welcome Home</div>
          <h2 style={{
            fontFamily: TOKENS.serif, fontWeight: 300,
            fontSize: 'clamp(2.4rem, 6vw, 5rem)', lineHeight: 1.05,
            color: '#fff', letterSpacing: '-0.015em', margin: 0,
            textShadow: '0 2px 30px rgba(46,53,48,0.15)',
          }}>
            Your journey <span style={{ fontStyle: 'italic' }}>begins here</span>.
          </h2>
          <p style={{ marginTop: 24, fontSize: 'clamp(1rem, 1.6vw, 1.2rem)', lineHeight: 1.7, color: 'rgba(255,255,255,0.92)', fontWeight: 300, maxWidth: 580, margin: '24px auto 0' }}>
            Take the first step toward a healthier, more mindful life. The community is already breathing with you.
          </p>
          <div style={{ marginTop: 44, display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            <motion.button
  onClick={() => window.location.href = '/login'}
  whileHover={{ y: -2, boxShadow: '0 18px 40px rgba(46,53,48,0.3)' }}
  whileTap={{ scale: 0.98 }}
  style={{ padding: '16px 38px', borderRadius: 999, border: 'none', cursor: 'pointer', background: TOKENS.ink, color: '#fff', fontWeight: 600, fontSize: '0.98rem', letterSpacing: '0.04em' }}
>
  Join the Community
</motion.button>
           <a href="https://yogarefer.com/" target="_blank" rel="noopener noreferrer">
  <motion.button
    whileHover={{ y: -2, boxShadow: '0 18px 40px rgba(201,162,75,0.45)' }}
    whileTap={{ scale: 0.98 }}
    style={{
      padding: '16px 36px', borderRadius: 999, border: 'none', cursor: 'pointer',
      background: `linear-gradient(135deg, ${TOKENS.gold} 0%, #d9b66a 100%)`,
      color: '#1c1f1b', fontWeight: 600, fontSize: '0.98rem', letterSpacing: '0.04em',
      boxShadow: '0 10px 30px rgba(201,162,75,0.35)',
    }}
  >
    Explore Yoga
  </motion.button>
</a>
          </div>
        </motion.div>
      </section>

      {/* ============ FAQ ============ */}
      <section style={{ position: 'relative', padding: 'clamp(80px, 12vw, 150px) clamp(24px, 6vw, 80px)', background: `linear-gradient(180deg, ${TOKENS.warmWhite} 0%, ${TOKENS.cream} 100%)`, overflow: 'hidden' }}>
        <Blob style={{ width: 340, height: 340, top: '-6%', right: '-4%', background: 'rgba(201,192,220,0.35)' }} />
        <Blob style={{ width: 280, height: 280, bottom: '-6%', left: '-4%', background: 'rgba(169,191,163,0.3)' }} delay={2} />
        <div style={{ position: 'relative', maxWidth: 880, margin: '0 auto' }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.9 }}
            style={{ textAlign: 'center', marginBottom: 56 }}
          >
            <div style={eyebrow}>· Questions</div>
            <h2 style={sectionTitle}>
              Frequently asked <span style={{ fontStyle: 'italic', color: TOKENS.sageDeep }}>questions</span>
            </h2>
          </motion.div>

          {FAQ_GROUPS.map((group, gi) => (
            <motion.div
              key={group.title}
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.8, delay: gi * 0.1 }}
              style={{ marginBottom: 40 }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
                <span style={{ width: 28, height: 2, background: TOKENS.gold, borderRadius: 2 }} />
                <h3 style={{ margin: 0, fontFamily: TOKENS.serif, fontSize: 'clamp(1.4rem, 2.6vw, 1.8rem)', fontWeight: 500, color: TOKENS.ink }}>
                  {group.title}
                </h3>
              </div>
              <div style={{ display: 'grid', gap: 12 }}>
                {group.items.map((item, ii) => {
                  const id = `${gi}-${ii}`;
                  return (
                    <FaqItem
                      key={id}
                      q={item.q}
                      a={item.a}
                      open={openFaq === id}
                      onToggle={() => setOpenFaq(openFaq === id ? null : id)}
                    />
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ============ 10. PREMIUM FOOTER ============ */}
      {/* <footer style={{ background: TOKENS.ink, color: 'rgba(255,255,255,0.78)', padding: 'clamp(70px, 10vw, 110px) clamp(24px, 6vw, 80px) 40px' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 50, paddingBottom: 60, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
            <div>
              <div style={{ fontFamily: TOKENS.serif, fontSize: '1.8rem', color: '#fff', fontWeight: 400, letterSpacing: '-0.01em' }}>yogarefer</div>
              <p style={{ marginTop: 18, fontSize: '0.92rem', lineHeight: 1.7, color: 'rgba(255,255,255,0.6)', fontWeight: 300 }}>
                A warm place to find your practice, your teacher, and your people.
              </p>
            </div>

            {[
              { title: 'About', links: ['Our Story', 'Mission', 'Press', 'Careers'] },
              { title: 'Community', links: ['Teachers', 'Practitioners', 'Events', 'Forum'] },
              { title: 'Resources', links: ['Guided Practices', 'Meditation', 'Articles', 'Newsletter'] },
              { title: 'Contact', links: ['hello@yogarefer.com', 'Help Center', 'Partnerships', 'Visit Us'] },
            ].map((col) => (
              <div key={col.title}>
                <div style={{ fontSize: '0.78rem', letterSpacing: '0.25em', textTransform: 'uppercase', color: TOKENS.gold, fontWeight: 500, marginBottom: 20 }}>{col.title}</div>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 12 }}>
                  {col.links.map((l) => (
                    <li key={l}>
                      <a href="#" style={{ color: 'rgba(255,255,255,0.72)', textDecoration: 'none', fontSize: '0.92rem', fontWeight: 300, transition: 'color 0.3s' }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = '#fff')}
                        onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.72)')}
                      >
                        {l}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div style={{ paddingTop: 40, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 20 }}>
            <p style={{ margin: 0, fontSize: '0.82rem', color: 'rgba(255,255,255,0.5)' }}>
              © {new Date().getFullYear()} YogaRefer. Breathe deeply.
            </p>
            <div style={{ display: 'flex', gap: 22, fontSize: '0.85rem' }}>
              {['Instagram', 'YouTube', 'Spotify', 'Substack'].map((s) => (
                <a key={s} href="#" style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none', letterSpacing: '0.05em' }}>{s}</a>
              ))}
            </div>
          </div>
        </div>
      </footer> */}
    </div>
  );
};

export default Home;
